// SearchServiceLocation.vue
<script lang="ts" setup>

    import { computed, ref, watch } from "vue";
    import SearchBase  from './SearchBase.vue';
    import { serviceLocation, IServiceLocationSearchKey } from '../../lib/ChoiceApi';
    import { IServiceLocationSearchItem, IServiceLocationSearchInputs } from '../../lib/Api/ServiceLocation';
    import { ISearchBaseColumnItem, ISearchBaseInputs } from "../../lib/Models/SearchModel";
    import { AddressDisplayMode } from "./SearchEnums";

    defineExpose({
        clearServiceLocationSearch
    });
    
    const searchServiceLocationBase = ref<InstanceType<typeof SearchBase> | null>(null); 
    const emit = defineEmits<{
        (e: "onChange", selectedItem: IServiceLocationSearchItem): void
    }>();
    
    const props = withDefaults(defineProps<{
        // Placeholder text to be displayed inside text control if no values
        placeholder?: string,
        // Label for the component
        label?: string,
        // The default search value 
        defaultSearchKey?: IServiceLocationSearchKey,
        // The maximum height of the search results
        maxHeight?: number,
        // Reset Focus
        resetFocus?: boolean,
        // Perform auto-search on text input
        enableAutoSearch?: boolean,
        // Show the clear search function
        showClearSearch?: boolean,
        // Limit results to locations with claims
        limitToClaimLocations?: boolean,
        // Indicate how to handle address -- Default is address 1 only
        addressDisplayMode?: AddressDisplayMode,
        // Add to Base Class
        addToBaseClass?: string,
        // A method to generate an address field value
        generateAddress?: (locationItem: IServiceLocationSearchItem) => string
    }>(), {
        placeholder: "Search Service Location",
        label: "Service Location",
        addToBaseClass: "",
        addressDisplayMode: AddressDisplayMode.Address1Only
    });

    interface IServiceLocationDisplayItem extends IServiceLocationSearchItem
    {
        address?: string    // Varies depending on how the user wants to display the address
    }

    const searchColumns = computed<ISearchBaseColumnItem[]>(() =>{
        const columns = [
            { title: "Service Location", element: "service_location_name" },
            { title: "Id", element: "entity_id" },
        ];

        switch(props.addressDisplayMode)
        {
            case AddressDisplayMode.Address1Only:
            case AddressDisplayMode.Address1And2Combined:        
            columns.push({ title: "Address", element: "address" });
            break;

            case AddressDisplayMode.Address1And2Seperate:
            columns.push(
                { title: "Address 1", element: "address1" },
                { title: "Address 2", element: "address2" });
            break;

            default:
                alert("Unexpected addressDisplayMode value: " + props.addressDisplayMode);
        }

        columns.push(
            { title: "City", element: "city" },
            { title: "State", element: "state" },
            { title: "Zip", element: "zip_code" },
            { title: "Billable", element: "is_billable" });
        return columns;
    });
    
    const searchResults = ref<IServiceLocationDisplayItem[]>([])
    const defaultSearchText = ref<string>("");
    const processing = ref<boolean>(false);

    const defaultAddToBaseClass = computed<string>(() => props.addToBaseClass);

    watch(() => props.defaultSearchKey, (newValue, oldValue) => {
        defaultSearchText.value = "";
        if (newValue?.display) {
            defaultSearchText.value = newValue.display;
            const selectedItem: IServiceLocationSearchItem = {
                service_location_name: newValue.display
            };
            onChange(selectedItem);
        }
/*
        else if (newValue?.patientId) {
            const searchInputs: IServiceLocationSearchInputs = { 
                patientId: newValue.patientId, 
                patientEid: newValue.patientEid, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
*/
        else if (newValue?.searchText) {
            defaultSearchText.value = newValue.searchText;
            const selectedItem: IServiceLocationSearchItem = {};
            onChange(selectedItem);
            const searchInputs: IServiceLocationSearchInputs = { 
                searchText: newValue.searchText, 
                displayAllResults: false 
            };
            processing.value = true;
            performSearch(searchInputs);
        }
        else  {
            defaultSearchText.value = "";
            const selectedItem: IServiceLocationSearchItem = {};
            onChange(selectedItem);
        }
    });

    async function performSearch(inputs: IServiceLocationSearchInputs) {
        inputs.limitToClaimLocations = props.limitToClaimLocations;
        const respSearch = await serviceLocation.search(inputs);

        // Generate the address value from the returned data
        const displayItems : IServiceLocationDisplayItem[] = respSearch ?? [];
        for(const displayItem of displayItems)
        {
            if (!props.generateAddress)
            {
                displayItem.address = displayItem.address1;
            }
            else
            {
                displayItem.address = props.generateAddress(displayItem);
            }
        }
        searchResults.value = displayItems;
    }

    async function performSearchBase(inputs: ISearchBaseInputs)  {
        const searchInputs: IServiceLocationSearchInputs = { 
            searchText: inputs.searchText,
            displayAllResults: false 
        };
        performSearch(searchInputs);
    }

    function onChange(selectedItem: IServiceLocationSearchItem) {
        emit("onChange", selectedItem);
    }

    function clearServiceLocationSearch() { 
        if (searchServiceLocationBase.value) {
            searchServiceLocationBase.value.clearSearch();
        }
    }
</script>

<template>
    <search-base 
        ref="searchServiceLocationBase"
        :placeholder="placeholder" 
        :label="label" 
        :columns="searchColumns" 
        displayColumn="service_location_name"
        :searchResults="searchResults"
        :defaultSearchText="defaultSearchText"
        :processing="processing"
        :enableAutoSearch="enableAutoSearch"
        :showClearSearch="showClearSearch"
        :resetFocus="resetFocus"
        :addToBaseClass="defaultAddToBaseClass"
        @performSearchBase="performSearchBase"
        @onChange="onChange">
    </search-base>
</template>